'use client';

import { Avatar, Button } from '@common/components';
import { SvgLike, SvgLikeFill, SvgWelloHelperMark } from '@common/icon';
import { useState } from 'react';

import type { Comment } from '@/api/getCommentList';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES, SELECTOR } from '@/constants';

import SvgBestIcon from './_assets/best.svg';
import SvgPickedIcon from './_assets/picked.svg';
import { CommentMenu, type CommentMenuProps } from './CommentMenu';

interface CommentCardProps {
  comment: Comment;
  onClickLikeButton?: () => void;
  onClickReplyButton?: () => void;
  children?: React.ReactNode;
  initialChildrenOpened?: boolean;
  initialRelativePolicyOpened?: boolean;
  onClickEditButton?: CommentMenuProps['onClickEditButton'];
  onClickPickButton?: () => void;
  hasMenu?: boolean;
  hasPickButton?: boolean;
}

export const CommentCard = ({
  onClickLikeButton,
  onClickReplyButton,
  comment,
  children,
  initialChildrenOpened,
  initialRelativePolicyOpened = true,
  hasMenu,
  onClickEditButton,
  onClickPickButton,
  hasPickButton,
}: CommentCardProps) => {
  const hasRelativePolicy = comment.hasRelativePolicy;

  const [isChildrenOpened, setIsChildrenOpened] = useState(
    comment.hasRelativePolicy || initialChildrenOpened,
  );

  const [isRelativePolicyOpened, setIsRelativePolicyOpened] = useState(
    initialRelativePolicyOpened,
  );

  const { parentNickName, contents } = comment;
  const isHiddenComment =
    comment.isBlocked ||
    comment.isDeleted ||
    comment.isReport ||
    !comment.isDisplay;

  const hasBadge = comment.isBest || comment.isPicked;

  const id = SELECTOR.COMMENT_PREFIX + comment.commentId;

  return (
    <>
      {isHiddenComment ? (
        <article
          className="relative flex w-full items-center justify-center rounded-xsmall bg-Gray-50 p-10 text-center text-Gray-500 typo-body4"
          id={id}
        >
          {comment.contents}
          {children ? (
            <Button
              className="absolute right-10 font-400 text-Gray-700 typo-caption1"
              dimensions="H26"
              theme="sub-text"
              onClick={() => setIsChildrenOpened((prev) => !prev)}
            >
              {isChildrenOpened ? '답글접기' : '답글보기'}
            </Button>
          ) : null}
        </article>
      ) : (
        <article className="flex gap-10 overflow-hidden bg-Gray-white" id={id}>
          <Avatar alt={`${comment.nickname} 프로필`} src={comment.profileImage} />
          <section className="flex flex-1 flex-col gap-4">
            <header className="flex justify-between">
              <section className="flex w-full flex-col gap-8">
                <div className="flex w-full items-center gap-6">
                  <span className="font-500 text-Gray-900 typo-body4">
                    {comment.nickname}
                  </span>
                  {comment.isWelloHelper ? (
                    <SvgWelloHelperMark originalFill className="size-16" />
                  ) : null}
                  <time className="text-Gray-700 typo-caption1">
                    {comment.createdAt}
                  </time>
                  <div className="ml-auto flex-center">
                    {hasPickButton && comment.isWelloHelper ? (
                      <Button
                        className="text-B-500 !typo-caption1"
                        dimensions="H26"
                        theme="sub-text"
                        onClick={onClickPickButton}
                      >
                        답변채택
                      </Button>
                    ) : null}
                    {hasMenu ? (
                      <CommentMenu
                        commentId={comment.commentId}
                        isMyComment={comment.isMine}
                        onClickEditButton={onClickEditButton}
                      />
                    ) : null}
                  </div>
                </div>
                {hasBadge ? (
                  <ul className="flex gap-4">
                    {comment.isBest ? (
                      <li className="flex size-fit items-center justify-center gap-4 rounded-xxsmall bg-Y-100 px-6 py-2 text-Y-800 typo-caption1">
                        <SvgBestIcon className="size-12" />
                        베스트댓글
                      </li>
                    ) : null}
                    {comment.isPicked ? (
                      <li className="flex size-fit items-center justify-center gap-4 rounded-xxsmall bg-Y-100 px-6 py-2 text-Y-800 typo-caption1">
                        <SvgPickedIcon className="size-12" />
                        채택된 답변
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </section>
            </header>
            <section className="whitespace-pre-wrap break-all text-Gray-800 typo-body4">
              {parentNickName ? (
                <span className="mr-8 text-B-500">@{parentNickName}</span>
              ) : null}
              {contents}
            </section>
            <footer className="flex justify-between gap-8 pt-4">
              <div className="flex h-fit items-center gap-16 leading-5">
                <button
                  aria-label="좋아요"
                  className="group flex gap-4 font-400 text-Gray-700 typo-caption1"
                  type="button"
                  onClick={onClickLikeButton}
                >
                  {comment.isMyThumbUp ? (
                    <SvgLikeFill className="mt-2 size-14 text-B-500" />
                  ) : (
                    <SvgLike className="mt-2 size-14 text-Gray-500" />
                  )}
                  좋아요 {comment.thumbUpCount}
                </button>
                <button
                  aria-label="답글"
                  className="font-400 text-Gray-700 typo-caption1"
                  type="button"
                  onClick={onClickReplyButton}
                >
                  답글쓰기
                </button>
              </div>
              {children && !hasRelativePolicy ? (
                <button
                  className="font-400 text-Gray-700 typo-caption1"
                  type="button"
                  onClick={() => setIsChildrenOpened((prev) => !prev)}
                >
                  {isChildrenOpened ? '답글접기' : '답글보기'}
                </button>
              ) : null}
              {hasRelativePolicy ? (
                <button
                  className="font-600 text-Gray-700 typo-caption1"
                  type="button"
                  onClick={() => setIsRelativePolicyOpened((prev) => !prev)}
                >
                  {isRelativePolicyOpened ? '관련정책 접기' : '관련정책 보기'}
                </button>
              ) : null}
            </footer>
            {isRelativePolicyOpened && hasRelativePolicy ? (
              <ul className="my-16 flex w-full flex-col gap-8">
                {comment.relationPolicyList.map((policy) => (
                  <li key={policy.metaPolicyIdIdx}>
                    <CustomLink
                      className="flex items-center gap-12 rounded-small border border-Gray-300 px-12 py-8"
                      href={ROUTES.POLICY_DETAIL.pathname({
                        idIdx: policy.metaPolicyIdIdx,
                      })}
                    >
                      <div className="relative size-36 overflow-hidden rounded-small border border-Gray-300 p-4">
                        <Image
                          alt={`${policy.policyName} 기관 로고`}
                          className="object-contain"
                          fill
                          sizes="36px"
                          src={policy.agencyLogo}
                        />
                      </div>
                      <strong className="w-0 flex-1 overflow-hidden truncate font-500 text-Gray-900 typo-body4">
                        {policy.policyName}
                      </strong>
                    </CustomLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </article>
      )}
      {isChildrenOpened ? children : null}
    </>
  );
};
